//-------------------------------------------------------------------------------------------------- Imports
import { Card, Col, Row, Typography } from "antd";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { StyleSheet } from "../../StyleSheet";
import SubDefaultLayout from "../../components/SubDefaultLayout";
import Loading from "../../components/Loading";
import { AppDispatch } from "../../stores/store";
import {
  getInternetSeguroAccount,
  getInternetSeguroUserDevices,
  selectInternetSeguroDevices,
} from "../../stores/slices/internetSeguroSlice";
import { ResponseInternetSeguroAccountDetail } from "../../types/common";
import { getDeviceIcon } from "./deviceIcon";
const { Text } = Typography;

//-------------------------------------------------------------------------------------------------- View
const ParentalControlDevices = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const account = useSelector(
    (state: any) =>
      state.internetSeguro
        .account as ResponseInternetSeguroAccountDetail | null,
  );
  const accountLoaded = useSelector(
    (state: any) => state.internetSeguro.accountLoaded,
  );
  const userDevicesLoaded = useSelector(
    (state: any) =>
      state.internetSeguro.userDevicesLoaded as Record<string, boolean>,
  );
  const devices = useSelector(selectInternetSeguroDevices);
  const users = account?.users ?? [];

  const devicesLoading =
    !accountLoaded ||
    (users.length > 0 && users.some((user) => !userDevicesLoaded[user.userId]));

  useEffect(() => {
    if (!accountLoaded) {
      dispatch(getInternetSeguroAccount());
    }
  }, [dispatch, accountLoaded]);

  useEffect(() => {
    if (users.length > 0) {
      Promise.all(
        users.map((user) =>
          dispatch(getInternetSeguroUserDevices(user.userId)),
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, accountLoaded, users.length]);

  if (devicesLoading) {
    return <Loading />;
  }

  return (
    <SubDefaultLayout
      title={"Dispositivos"}
      needReturn={true}
      navigateTo="/parental-control"
    >
      <div style={styles.listContainer}>
        {devices.length === 0 ? (
          <Text style={styles.emptyText}>
            Aún no hay dispositivos registrados
          </Text>
        ) : (
          devices.map((item, index) => {
            const DeviceIcon = getDeviceIcon(item.device);
            return (
              <Card
                key={item.device.registrationId || index}
                style={styles.cardStyles}
                onClick={() =>
                  navigate(`/parental-control/users/${item.user.userId}`)
                }
              >
                <Row align="middle">
                  <Col style={styles.iconContainer}>
                    <DeviceIcon style={styles.deviceIcon} />
                  </Col>
                  <Col flex="auto" style={styles.titleContainer}>
                    <Text style={styles.textName}>{item.device.name}</Text>
                    <Text style={styles.textSub}>
                      {[item.device.manufacturer, item.device.model]
                        .filter(Boolean)
                        .join(" ")}
                    </Text>
                    <Text style={styles.textOwner}>{item.user.username}</Text>
                  </Col>
                </Row>
              </Card>
            );
          })
        )}
      </div>
    </SubDefaultLayout>
  );
};

//-------------------------------------------------------------------------------------------------- Styles
const styles = StyleSheet.create({
  listContainer: {
    width: "50%",
    margin: "0 auto",
    marginTop: 50,
  },
  cardStyles: {
    marginBottom: 15,
    boxShadow: "0px 0px 8px #00000029",
    borderRadius: 15,
    cursor: "pointer",
  },
  iconContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingRight: 15,
  },
  deviceIcon: {
    fontSize: 28,
    color: "#0055c2",
    marginRight: 10,
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
  },
  textName: {
    fontWeight: "bold",
    fontSize: "1.50em",
    color: "#0055c1",
  },
  textSub: {
    fontWeight: 600,
  },
  textOwner: {
    color: "#0055c2",
    fontSize: 13,
  },
  emptyText: {
    display: "block",
    textAlign: "center",
    paddingVertical: 30,
  },
});

//-------------------------------------------------------------------------------------------------- Exports
export default ParentalControlDevices;
